import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Filter, Bug, Eye, EyeOff } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { AppState } from '../types';
interface ErrorLogPanelProps {
  state: AppState;
  isOpen: boolean;
  onClose: () => void;
}
type ErrorFilter = 'all' | 'rendering' | 'missing_explanation' | 'visual_aid' | 'shuffling';
export function ErrorLogPanel({
  state,
  isOpen,
  onClose
}: ErrorLogPanelProps) {
  const [filter, setFilter] = useState<ErrorFilter>('all');
  // Get all errors from the error log
  const allErrors = state.errorLog || [];
  // Filter errors based on selected filter
  const filteredErrors = allErrors.filter(error => {
    if (filter === 'all') return true;
    return error.errorType === filter;
  });
  // Count errors by type
  const errorCounts = {
    all: allErrors.length,
    rendering: allErrors.filter(e => e.errorType === 'rendering').length,
    missing_explanation: allErrors.filter(e => e.errorType === 'missing_explanation').length,
    visual_aid: allErrors.filter(e => e.errorType === 'visual_aid').length,
    shuffling: allErrors.filter(e => e.errorType === 'shuffling').length
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-471">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-472">
        <motion.div initial={{
        scale: 0.95,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.95,
        opacity: 0
      }} onClick={e => e.stopPropagation()} className="w-full max-w-4xl max-h-[80vh] overflow-hidden" data-id="element-473">
          <Card className="p-6" data-id="element-474">
            {/* Header */}
            <div className="flex items-center justify-between mb-6" data-id="element-475">
              <div className="flex items-center gap-3" data-id="element-476">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center" data-id="element-477">
                  <Bug className="w-5 h-5 text-amber-600" data-id="element-478" />
                </div>
                <div data-id="element-479">
                  <h2 className="text-xl font-bold text-slate-900" data-id="element-480">
                    Error Log
                  </h2>
                  <p className="text-sm text-slate-600" data-id="element-481">
                    {allErrors.length}{' '}
                    {allErrors.length === 1 ? 'error' : 'errors'} logged
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors" data-id="element-482">
                <X className="w-5 h-5 text-slate-400" data-id="element-483" />
              </button>
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2 mb-6" data-id="element-484">
              <FilterButton active={filter === 'all'} onClick={() => setFilter('all')} count={errorCounts.all} label="All Errors" data-id="element-485" />
              <FilterButton active={filter === 'rendering'} onClick={() => setFilter('rendering')} count={errorCounts.rendering} label="Rendering" color="rose" data-id="element-486" />
              <FilterButton active={filter === 'missing_explanation'} onClick={() => setFilter('missing_explanation')} count={errorCounts.missing_explanation} label="Missing Explanation" color="amber" data-id="element-487" />
              <FilterButton active={filter === 'visual_aid'} onClick={() => setFilter('visual_aid')} count={errorCounts.visual_aid} label="Visual Aid" color="purple" data-id="element-488" />
              <FilterButton active={filter === 'shuffling'} onClick={() => setFilter('shuffling')} count={errorCounts.shuffling} label="Shuffling" color="blue" data-id="element-489" />
            </div>

            {/* Error List */}
            <div className="space-y-3 max-h-96 overflow-y-auto" data-id="element-490">
              {filteredErrors.length === 0 ? <div className="text-center py-12" data-id="element-491">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-492">
                    <Eye className="w-8 h-8 text-emerald-600" data-id="element-493" />
                  </div>
                  <p className="text-slate-600 font-medium" data-id="element-494">No errors found!</p>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-495">
                    {filter === 'all' ? 'Everything is working perfectly.' : `No ${filter.replace('_', ' ')} errors.`}
                  </p>
                </div> : filteredErrors.map((error, index) => <ErrorItem key={index} error={error} data-id="element-496" />)}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-497">
              <Button onClick={onClose} variant="secondary" fullWidth data-id="element-498">
                Close Error Log
              </Button>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  count: number;
  label: string;
  color?: 'rose' | 'amber' | 'purple' | 'blue';
}
function FilterButton({
  active,
  onClick,
  count,
  label,
  color
}: FilterButtonProps) {
  const colorClasses = {
    rose: 'bg-rose-100 text-rose-700 border-rose-300',
    amber: 'bg-amber-100 text-amber-700 border-amber-300',
    purple: 'bg-purple-100 text-purple-700 border-purple-300',
    blue: 'bg-blue-100 text-blue-700 border-blue-300'
  };
  const activeClass = color ? colorClasses[color] : 'bg-slate-900 text-white border-slate-900';
  const inactiveClass = 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50';
  return <button onClick={onClick} className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${active ? activeClass : inactiveClass}`} data-id="element-499">
      <span className="flex items-center gap-2" data-id="element-500">
        <Filter className="w-4 h-4" data-id="element-501" />
        {label}
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${active ? 'bg-white/20' : 'bg-slate-100'}`} data-id="element-502">
          {count}
        </span>
      </span>
    </button>;
}
interface ErrorItemProps {
  error: {
    questionId: string;
    errorType: string;
    userChoice: number;
    timestamp: number;
  };
}
function ErrorItem({
  error
}: ErrorItemProps) {
  const errorTypeLabels: Record<string, {
    label: string;
    color: string;
    icon: React.ReactNode;
  }> = {
    rendering: {
      label: 'Rendering Error',
      color: 'rose',
      icon: <AlertTriangle className="w-4 h-4" data-id="element-503" />
    },
    missing_explanation: {
      label: 'Missing Explanation',
      color: 'amber',
      icon: <AlertTriangle className="w-4 h-4" data-id="element-504" />
    },
    visual_aid: {
      label: 'Visual Aid Error',
      color: 'purple',
      icon: <EyeOff className="w-4 h-4" data-id="element-505" />
    },
    shuffling: {
      label: 'Shuffling Error',
      color: 'blue',
      icon: <AlertTriangle className="w-4 h-4" data-id="element-506" />
    },
    investigating: {
      label: 'Wrong Answer',
      color: 'slate',
      icon: <AlertTriangle className="w-4 h-4" data-id="element-507" />
    }
  };
  const errorInfo = errorTypeLabels[error.errorType] || errorTypeLabels.investigating;
  const colorClasses = {
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700'
  };
  const colorClass = colorClasses[errorInfo.color as keyof typeof colorClasses];
  return <div className={`p-4 rounded-lg border-2 ${colorClass}`} data-id="element-508">
      <div className="flex items-start justify-between mb-2" data-id="element-509">
        <div className="flex items-center gap-2" data-id="element-510">
          {errorInfo.icon}
          <span className="font-bold text-sm" data-id="element-511">{errorInfo.label}</span>
        </div>
        <span className="text-xs opacity-75" data-id="element-512">
          {new Date(error.timestamp).toLocaleString()}
        </span>
      </div>
      <div className="space-y-1 text-sm" data-id="element-513">
        <p data-id="element-514">
          <strong data-id="element-515">Question ID:</strong> {error.questionId}
        </p>
        <p data-id="element-516">
          <strong data-id="element-517">User Choice:</strong> Option {error.userChoice + 1}
        </p>
      </div>
    </div>;
}